import { ChevronRightIcon } from '@heroicons/react/outline';
import { ChevronDownIcon, ChevronLeftIcon, TimeIcon } from 'components/icons';
import { ButtonCTASmall } from 'components/ui';
import { subscriptionAccess } from 'config.js';
import {
  differenceInDays,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  getMonth,
  getYear,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from 'date-fns';
import { range } from 'lodash';
import querystring from 'querystring';
import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppSelector } from 'store';
import { SubscriptionLevel, userDetailsState } from 'store/userDetailsSlice';
import { useParseURLQueryParams } from 'utils';

interface DateSelectorProps {
  defaultPeriod?: 'Last 7 days' | 'Last 30 days' | 'Last 365 days';
  align?: 'left' | 'right';
}
export default function DateSelector({
  defaultPeriod = 'Last 30 days',
  align = 'right',
}: DateSelectorProps) {
  const [modalOpen, setmodalOpen] = useState(false);
  const history = useHistory();

  const { query } = useParseURLQueryParams();

  const { currentStore } = useAppSelector(userDetailsState);

  const storeSubscriptionPlan = currentStore.subscriptionPlan
    ?.level as SubscriptionLevel;

  // Pre defined periods state, object and function
  const [selectedPreDefinedPeriod, setselectedPreDefinedPeriod] = useState<
    DateSelectorProps['defaultPeriod'] | 'Custom'
  >(defaultPeriod);

  useEffect(() => {
    if (history.location.search && (query?.start_date || query?.end_date)) {
      setselectedPreDefinedPeriod('Custom');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preDefinedPeriods: { [x: string]: any } = {
    Yesterday: subDays(new Date(), 1),
    'Last 7 days': subDays(new Date(), 7),
    'Last 30 days': subDays(new Date(), 30),
    'Last 90 days': subDays(new Date(), 90),
    'Last 365 days': subDays(new Date(), 365),
    'Last 730 days': subDays(new Date(), 730),
    'Last week': {
      start: startOfWeek(subWeeks(new Date(), 1)),
      end: endOfWeek(subWeeks(new Date(), 1)),
    },
    'Last month': {
      start: startOfMonth(subMonths(new Date(), 1)),
      end: endOfMonth(subMonths(new Date(), 1)),
    },
    'Last quarter': {
      start: startOfQuarter(subQuarters(new Date(), 1)),
      end: endOfQuarter(subQuarters(new Date(), 1)),
    },
    'Last year': {
      start: startOfYear(subYears(new Date(), 1)),
      end: endOfYear(subYears(new Date(), 1)),
    },
    'Year to date': new Date(new Date().getFullYear(), 0, 1),
  };

  function selectpreDefinedPeriods(e: any) {
    const referenceDates = [
      'Last month',
      'Last year',
      'Last 2 years',
      'Last week',
      'Last quarter',
    ];
    setselectedPreDefinedPeriod(e.target.innerHTML);

    if (referenceDates.includes(e.target.innerHTML)) {
      setStartDate(preDefinedPeriods[e.target.innerHTML].start);
      setEndDate(preDefinedPeriods[e.target.innerHTML].end);
      return;
    }

    setStartDate(preDefinedPeriods[e.target.innerHTML]);
    setEndDate(new Date());
  }

  // Calendar States and functions

  // Check if there is start and end date in URL query params
  const start_date = query?.start_date && Date.parse(query?.start_date);
  const end_date = query?.end_date && Date.parse(query?.end_date);

  const [startDate, setStartDate] = useState(
    start_date || preDefinedPeriods[defaultPeriod]
  );
  const [endDate, setEndDate] = useState(end_date || new Date());

  function onChangeStartDate(value: any) {
    setStartDate(value);
  }
  function onChangeEndDate(value: any) {
    setEndDate(value);
  }

  function handleModalOpenClose() {
    if (!modalOpen) {
      setmodalOpen(true);
      return;
    }
    // Close and Reset fields/state on close without applying
    setmodalOpen(false);
    setStartDate(start_date || preDefinedPeriods[defaultPeriod]);
    setEndDate(end_date || new Date());
    setselectedPreDefinedPeriod(
      query?.start_date || query?.end_date ? 'Custom' : defaultPeriod
    );
  }

  const toastUser = (message: string) => {
    toast.info(message, {
      autoClose: 15000,
      toastId: 'date-paywall',
    });
  };

  function handleApplyDates() {
    const isDateSelectorDisabled =
      storeSubscriptionPlan < subscriptionAccess.dateSelector;
    const isSmallBusinessPlan =
      storeSubscriptionPlan === SubscriptionLevel.small_business;

    const isGrowthPlan = storeSubscriptionPlan === SubscriptionLevel.growth;

    const startDateToTodayInDays = differenceInDays(new Date(), startDate);

    if (isDateSelectorDisabled) {
      handleModalOpenClose();
      setselectedPreDefinedPeriod(defaultPeriod);
      toastUser('Date selection requires an upgraded plan.');
      return;
    }

    if (isSmallBusinessPlan && startDateToTodayInDays > 365) {
      handleModalOpenClose();
      setselectedPreDefinedPeriod(defaultPeriod);
      toastUser(
        'Small Business plan is limited to 1 year of your Shopify data.'
      );
      return;
    }

    if (isGrowthPlan && startDateToTodayInDays > 730) {
      handleModalOpenClose();
      setselectedPreDefinedPeriod(defaultPeriod);
      toastUser('Growth plan is limited to 2 years of your Shopify data.');
      return;
    }

    const updatedQuery = {
      ...query,
      start_date: format(startDate, 'yyyy-MM-dd'),
      end_date: format(endDate, 'yyyy-MM-dd'),
    };

    history.push({
      search: querystring.stringify(updatedQuery),
    });
    setmodalOpen(false);
  }

  // Send defaults if date not present, set paywall based on plan
  useEffect(() => {
    const defaultParams = {
      ...query,
      start_date: format(preDefinedPeriods[defaultPeriod], 'yyyy-MM-dd'),
      end_date: format(new Date(), 'yyyy-MM-dd'),
    };

    const noDateInURL =
      history.location.search && (!!!start_date || !!!end_date);

    const isDateSelectorDisabled =
      storeSubscriptionPlan < subscriptionAccess.dateSelector;

    const isSmallBusinessPlan =
      storeSubscriptionPlan === SubscriptionLevel.small_business;

    const isGrowthPlan = storeSubscriptionPlan === SubscriptionLevel.growth;

    const startDateToTodayInDays = differenceInDays(new Date(), startDate);

    if (
      noDateInURL ||
      isDateSelectorDisabled ||
      (isSmallBusinessPlan && startDateToTodayInDays > 365) ||
      (isGrowthPlan && startDateToTodayInDays > 730)
    ) {
      history.push({
        search: querystring.stringify(defaultParams),
      });
      setselectedPreDefinedPeriod(defaultPeriod);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [end_date, history.location.search, start_date, storeSubscriptionPlan]);

  function convertUTCToLocalDate(date: number | Date) {
    if (!date) {
      return date;
    }
    date = new Date(date);
    date = new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate()
    );
    return date;
  }

  const getMinDate = () => {
    const isDateSelectorDisabled =
      storeSubscriptionPlan < subscriptionAccess.dateSelector;

    const isSmallBusinessPlan =
      storeSubscriptionPlan === SubscriptionLevel.small_business;

    const isGrowthPlan = storeSubscriptionPlan === SubscriptionLevel.growth;

    if (isDateSelectorDisabled || isSmallBusinessPlan) {
      return subDays(new Date(), 365);
    }

    if (isGrowthPlan) {
      return subDays(new Date(), 730);
    }

    return null;
  };

  const preDefinedPeriodsMenu = Object.keys(preDefinedPeriods);

  const daysSelected = differenceInDays(endDate, startDate);

  return (
    <div className="">
      <button
        onClick={handleModalOpenClose}
        className="relative flex items-center py-1 mr-3 space-x-2 font-bold text-left focus:outline-none disabled:opacity-50 disabled:pointer-events-none"
      >
        <div>
          <TimeIcon className="w-5 h-5 text-gray-700" />
        </div>
        <span className="text-segments-link">
          {modalOpen ? 'Custom' : selectedPreDefinedPeriod} <span> | </span>
          {format(
            convertUTCToLocalDate(
              start_date ?? preDefinedPeriods[defaultPeriod]
            ),
            'yyyy-MM-dd'
          )}{' '}
          –{' '}
          {format(convertUTCToLocalDate(end_date ?? new Date()), 'yyyy-MM-dd')}
        </span>
        <div>
          <ChevronDownIcon className="w-5 h-5 text-black" />
        </div>
      </button>
      {modalOpen && (
        <>
          {/* Transparent Background to capture outside click and close it*/}
          <div
            className="fixed inset-0 z-10 bg-black bg-opacity-30"
            onClick={handleModalOpenClose}
          />
          <div
            className={`absolute ${
              align === 'left' ? 'left-0' : 'right-0'
            } z-50 p-6 mx-auto mt-3 bg-white border shadow-md pr-7 rounded-3xl`}
          >
            {/* Container */}
            <div className="flex divide-x ">
              <div className="w-32 space-y-3 text-base text-black text-opacity-30">
                {preDefinedPeriodsMenu.map((period) => (
                  <button
                    onClick={selectpreDefinedPeriods}
                    key={period}
                    className={`focus:outline-none disabled:opacity-50 ${
                      selectedPreDefinedPeriod === period &&
                      'text-segments-accent-color-1 font-bold'
                    }`}
                  >
                    {period}
                  </button>
                ))}
              </div>
              {/* Date Pickers */}
              <div className="pl-2">
                <div className="flex items-center justify-start pl-2">
                  <div>
                    <DatePicker
                      className="p-0 text-2xl font-normal leading-7 text-center text-black placeholder-black border-none w-36 placeholder-opacity-30"
                      selected={startDate}
                      tabIndex={1}
                      onChange={(date) => {
                        setStartDate(date);
                      }}
                      onFocus={() => setselectedPreDefinedPeriod('Custom')}
                      popperClassName="hidden"
                      dateFormat="yyyy-MM-dd"
                      placeholderText="yyyy-mm-dd"
                      maxDate={new Date()}
                      minDate={getMinDate()}
                    />
                  </div>
                  <hr className="w-5 border-gray-500" />
                  <div>
                    <DatePicker
                      className="p-0 text-2xl font-normal leading-7 text-center text-black placeholder-black border-none w-36 placeholder-opacity-20"
                      selected={endDate}
                      tabIndex={2}
                      onChange={(date) => setEndDate(date)}
                      onFocus={() => setselectedPreDefinedPeriod('Custom')}
                      popperClassName="hidden"
                      dateFormat="yyyy-MM-dd"
                      placeholderText="yyyy-mm-dd"
                      maxDate={new Date()}
                      minDate={startDate}
                    />
                  </div>
                </div>
                <div className="mt-4 ml-4 flex">
                  <DatePicker
                    key={startDate}
                    selected={startDate}
                    onChange={onChangeStartDate}
                    startDate={startDate}
                    onChangeRaw={() => setselectedPreDefinedPeriod('Custom')}
                    selectsStart
                    inline
                    disabledKeyboardNavigation
                    maxDate={new Date()}
                    minDate={getMinDate()}
                    calendarClassName="border-none bg-segments-gray-F8 rounded"
                    renderCustomHeader={CustomHeader}
                  />
                  <DatePicker
                    key={endDate}
                    selected={endDate}
                    onChange={onChangeEndDate}
                    endDate={endDate}
                    onChangeRaw={() => setselectedPreDefinedPeriod('Custom')}
                    selectsEnd
                    inline
                    disabledKeyboardNavigation
                    maxDate={new Date()}
                    minDate={startDate}
                    calendarClassName="border-none bg-segments-gray-F8 rounded"
                    renderCustomHeader={CustomHeader}
                  />
                </div>
                <div className="flex items-center justify-between pl-4 mt-4">
                  <div className="font-normal">
                    {daysSelected > 0 && (
                      <p className="text-black">
                        {daysSelected} {daysSelected > 1 ? 'days' : 'day'}
                        <span className="text-gray-500"> selected</span>{' '}
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-4 text-black">
                    <ButtonCTASmall
                      variant="secondary"
                      type="button"
                      onClick={handleModalOpenClose}
                    >
                      <p className="px-4 font-normal text-segments-13px">
                        Cancel
                      </p>
                    </ButtonCTASmall>
                    <ButtonCTASmall
                      variant="main"
                      type="button"
                      onClick={handleApplyDates}
                      disabled={!!!startDate || !!!endDate}
                    >
                      <p className="px-4 font-bold text-segments-13px">Apply</p>
                    </ButtonCTASmall>
                  </div>
                </div>
              </div>
              {/* End of Date Pickers */}
            </div>
            {/* End of Container */}
          </div>
        </>
      )}
    </div>
  );
}

function CustomHeader(params: {
  date: Date;
  changeYear(year: number): void;
  changeMonth(month: number): void;
  customHeaderCount: number;
  decreaseMonth(): void;
  increaseMonth(): void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
  decreaseYear(): void;
  increaseYear(): void;
  prevYearButtonDisabled: boolean;
  nextYearButtonDisabled: boolean;
}) {
  const years = range(1990, getYear(new Date()) + 1, 1);
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return (
    <div className="flex m-2 justify-center space-x-1">
      <button
        onClick={params.decreaseMonth}
        disabled={params.prevMonthButtonDisabled}
        className="disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ChevronLeftIcon className="h-5 w-5" />
      </button>
      <select
        className="border-none rounded"
        value={getYear(params.date)}
        onChange={({ target: { value } }) => params.changeYear(value as any)}
      >
        {years.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <select
        className="border-none rounded"
        value={
          months[
            getMonth(params.date) + params.customHeaderCount > 0
              ? getMonth(params.date) + params.customHeaderCount
              : 0
          ]
        }
        onChange={({ target: { value } }) =>
          params.changeMonth(months.indexOf(value))
        }
      >
        {months.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <button
        onClick={params.increaseMonth}
        disabled={params.nextMonthButtonDisabled}
        className="disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ChevronRightIcon className="h-5 w-5" />
      </button>
    </div>
  );
}
